import { AuthenticationService } from 'src/app/core/services/auth.service';
import { catchError, combineLatest, debounceTime, distinctUntilChanged, Observable, of, startWith, Subject, switchMap, takeUntil } from 'rxjs';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CompaniesService } from 'src/app/core/services/utelize/system/company/companies-service';
import { CompanyDTO } from 'src/app/core/models/system/company/company.dto';
import { ConfigSystemParams, DashboardProfit } from 'src/app/core/consts';
import { ConfigSystemService } from 'src/app/core/services/utelize/system/config-system/config-system-service';
import { DashboardService } from 'src/app/core/services/utelize/general/dashboard-service';
import { DashboardStatistic, DashboardStatisticSecondary } from 'src/app/core/interfaces/general/dashboard-statistic';
import { DashboardTotal_Previous_Current_PercentDTO, DashboardTotal_Previous_Current_PercentDTO_EmptyObject } from 'src/app/core/models/system/dashboard/dashboard-total-previous-current-percent.dto';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import { OurDate } from 'src/app/core/interfaces/general/our-date';
import { populateOurDate } from 'src/app/core/functions-generic/functions-generic-date';
import { WidgetService } from 'src/app/core/services/utelize/general/widget-service';
import { WidgetSMSAllowanceDTO, WidgetSMSAllowanceDTO_EmptyObject } from 'src/app/core/models/system/widget/widget-sms-allowance.dto';

const STAT_TITLE_ACTIVE_SIMS_CURRENT      ="Current Deployed SIMs";
const STAT_TITLE_CHARGES_SO_FAR_THIS_MONTH="Charges So Far This Month";
const STAT_TITLE_DATA_USAGE_THIS_MONTH    ="This Month's Data Usage";
const STAT_TITLE_SMS_ALLOWANCE            ="SMS Allowance";

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-widget-row-usage-dashboard-stats',
  templateUrl: './widget-row-usage-dashboard-stats.component.html',
  styleUrls: ['./widget-row-usage-dashboard-stats.component.scss']
})

export class WidgetRowUsageDashboardStatsComponent implements OnDestroy, OnInit {

  @Input() allowContainerClose: boolean | undefined;
  @Input() containerTitle     : string  | undefined;
  @Input() marginBottom       : string  | undefined="mb-0";
  @Input() useContainer       : boolean | undefined;
  @Input() useCountUp         : boolean=true;

  @Output() containerClosed: EventEmitter<void>=new EventEmitter<void>();

  get tbDisplayEnabled() { return this.configSystemService.getValueAsBoolean(ConfigSystemParams.DashboardUsageDisplayTbEnabled, true); }

  private destroy$       =new Subject<void>();
  private filterCompanyID: number  | undefined;
  private filterDate     : OurDate | undefined;
  private refreshNonce   : number=0;
  private selection$     =new Subject<{ companyId: number; date: { year: number; month: number; day: number }; nonce: number }>();

  private readonly USAGE_TB_THRESHOLD_GB: number=1024;

  public expandContainer : boolean=true;
  public maxWidgetsPerRow: number=4;
  public statData!       : Array<DashboardStatistic>;
  public visibleContainer: boolean=true;

  constructor(
    private authenticationService: AuthenticationService,
    private changeDetectorRef    : ChangeDetectorRef,
    private companiesService     : CompaniesService,
    private configSystemService  : ConfigSystemService,
    private dashboardService     : DashboardService,
    private exceptionsService    : ExceptionsService,
    private widgetService        : WidgetService
  ) {

  }

  public ngOnInit(): void {
    try {
      this.displayData();
      this.initSelectionStream();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngOnDestroy(): void {
    try {
      this.destroy$.next();
      this.destroy$.complete();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#region Private Methods

  private applySMSAllowance(data: WidgetSMSAllowanceDTO): void {
    try {
      if (!this.statData)
        this.displayData();

      const used     =data?.usedMonthlyQuantity      ?? 0;
      const total    =data?.totalMonthlyQuantity     ?? 0;
      const remaining=data?.remainingMonthlyQuantity ?? Math.max(0, total - used);

      this.statData.forEach((stat: DashboardStatistic) => {
        if (stat.title===STAT_TITLE_SMS_ALLOWANCE) {
          stat.loading =false;
          stat.subTitle=remaining.toLocaleString()+" left";
          stat.value   =used.toLocaleString()+" / "+total.toLocaleString();

          stat.iconColour=total>0 && remaining<=0 ? "danger" : "success";
        }
      });

      this.changeDetectorRef.markForCheck();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private applyTotalCostsUsageAndLicence(data: DashboardTotal_Previous_Current_PercentDTO[], company: CompanyDTO | undefined): void {
    try {
      if (!this.statData)
        this.displayData();

      const sorted         =this.sortByPreferredCurrency(data ?? [], company);
      const primary        =sorted[0];
      const secondaryItems =sorted.slice(1);

      this.statData.forEach((stat: DashboardStatistic) => {
        if (stat.title===STAT_TITLE_CHARGES_SO_FAR_THIS_MONTH) {
          stat.loading=false;

          if (primary) {
            const primarySymbol=(primary.currencySymbol ?? "").trim();
            stat.percentage    =<string><unknown>primary.percentageChange;
            stat.prefix        =primarySymbol.length>0 ? primarySymbol : "£";
            stat.profit        =primary.percentageChange===0
                                ? DashboardProfit.Equal
                                : (primary.percentageChange<0 ? DashboardProfit.Down : DashboardProfit.Up);
            stat.value         =primary.currentTotal;
          } else {
            stat.percentage=<string><unknown>"";
            stat.prefix    ="£";
            stat.profit    =DashboardProfit.Equal;
            stat.value     =0;
          }

          stat.secondaries=secondaryItems.map(entry => this.toStatSecondary(entry));
        }
      });

      this.changeDetectorRef.markForCheck();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private showUsageAsTb(stat: DashboardStatistic): boolean {
    try {
      if (stat?.unit!=='GB' || typeof stat.value!=='number' || !this.tbDisplayEnabled)
        return false;

      return stat.value>=this.USAGE_TB_THRESHOLD_GB;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  private sortByPreferredCurrency(list: DashboardTotal_Previous_Current_PercentDTO[], company: CompanyDTO | undefined): DashboardTotal_Previous_Current_PercentDTO[] {
    try {
      const companySymbol  =(company?.invoiceCurrencySymbol ?? "").trim();
      const preferenceOrder=[companySymbol, "£", "$", "€"]
                              .filter(s => s.length>0)
                              .filter((s, idx, arr) => arr.indexOf(s)===idx);

      return [...list].sort((a, b) => {
        const aIdx=preferenceOrder.indexOf((a?.currencySymbol ?? "").trim());
        const bIdx=preferenceOrder.indexOf((b?.currencySymbol ?? "").trim());

        if (aIdx===-1 && bIdx===-1) return 0;
        if (aIdx===-1) return  1;
        if (bIdx===-1) return -1;
        return aIdx - bIdx;
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return list;
    }
  }

  private toStatSecondary(entry: DashboardTotal_Previous_Current_PercentDTO): DashboardStatisticSecondary {
    try {
      const symbol=(entry?.currencySymbol ?? "").trim();
      return {
        percentage    : <string><unknown>entry.percentageChange,
        prefix        : symbol.length>0 ? symbol : "",
        profit        : entry.percentageChange===0
                        ? DashboardProfit.Equal
                        : (entry.percentageChange<0 ? DashboardProfit.Down : DashboardProfit.Up),
        profitUpIsGood: false,
        value         : entry.currentTotal
      };
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return { percentage: "", prefix: "", profit: DashboardProfit.Equal, profitUpIsGood: false, value: 0 };
    }
  }

  private applyTotalSimCount(data: DashboardTotal_Previous_Current_PercentDTO): void {
    try {
      if (!this.statData)
        this.displayData();

      this.statData.forEach((stat: DashboardStatistic) => {
        if (stat.title===STAT_TITLE_ACTIVE_SIMS_CURRENT) {
          stat.loading   =false;
          stat.percentage=<string><unknown>data.percentageChange;
          stat.profit    =data.percentageChange===0
                          ? DashboardProfit.Equal
                          : (data.percentageChange<0 ? DashboardProfit.Down : DashboardProfit.Up);
          stat.value     =data.currentTotal;

          stat.iconColour=(Number(data.currentTotal) || 0)>0 ? "success" : "danger";
        }
      });

      this.changeDetectorRef.markForCheck();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private applyTotalUsage(data: DashboardTotal_Previous_Current_PercentDTO): void {
    try {
      if (!this.statData)
        this.displayData();

      this.statData.forEach((stat: DashboardStatistic) => {
        if (stat.title===STAT_TITLE_DATA_USAGE_THIS_MONTH) {
          stat.loading   =false;
          stat.percentage=<string><unknown>data.percentageChange;
          stat.profit    =data.percentageChange===0
                          ? DashboardProfit.Equal
                          : (data.percentageChange<0 ? DashboardProfit.Down : DashboardProfit.Up);
          stat.value     =data.currentTotal;

          const shown    =Number((Number(data.currentTotal) || 0).toFixed(stat.decimalPlaces ?? 1));
          stat.iconColour=shown>0 ? "warning" : "success";
        }
      });

      this.changeDetectorRef.markForCheck();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private displayData(): void {
    try {
      this.statData=[];

      let statActiveSimsCurrent: DashboardStatistic={
        decimalPlaces : 0,
        icon          : "bi bi-sim",
        iconColour    : "success",
        link          : undefined,
        linkText      : undefined,
        loading       : true,
        percentage    : <string><unknown>"",
        profit        : "",
        profitUpIsGood: true,
        showProfit    : true,
        showSpinner   : false,
        title         : STAT_TITLE_ACTIVE_SIMS_CURRENT,
        value         : undefined
      };

      let statDataUsageThisMonth: DashboardStatistic={
        decimalPlaces : 1,
        icon          : "bi bi-bar-chart-line",
        iconColour    : "warning",
        link          : undefined,
        linkText      : undefined,
        loading       : true,
        percentage    : <string><unknown>"",
        profit        : "",
        profitUpIsGood: false,
        showProfit    : true,
        showSpinner   : false,
        title         : STAT_TITLE_DATA_USAGE_THIS_MONTH,
        unit          : "GB",
        value         : undefined
      };

      let statChargesSoFarThisMonth: DashboardStatistic={
        decimalPlaces : 2,
        icon          : "bi bi-cash-stack",
        iconColour    : "success",
        link          : undefined,
        linkText      : undefined,
        loading       : true,
        percentage    : <string><unknown>"",
        prefix        : "£",
        profit        : "",
        profitUpIsGood: false,
        showProfit    : true,
        showSpinner   : false,
        title         : STAT_TITLE_CHARGES_SO_FAR_THIS_MONTH,
        value         : undefined
      };

      let statSmsAllowance: DashboardStatistic={
        decimalPlaces : 0,
        icon          : "bi bi-chat-dots",
        iconColour    : "info",
        link          : undefined,
        linkText      : undefined,
        loading       : true,
        percentage    : <string><unknown>"",
        profit        : "",
        profitUpIsGood: true,
        showProfit    : false,
        showSpinner   : false,
        title         : STAT_TITLE_SMS_ALLOWANCE,
        value         : undefined
      };

      this.statData.push(statActiveSimsCurrent);
      this.statData.push(statDataUsageThisMonth);
      this.statData.push(statChargesSoFarThisMonth);
      this.statData.push(statSmsAllowance);

      this.changeDetectorRef.markForCheck();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private initSelectionStream(): void {
    try {
      this.selection$
      .pipe(
        debounceTime(150),
        distinctUntilChanged((a, b) =>
          a.companyId ===b.companyId  &&
          a.date.year ===b.date.year  &&
          a.date.month===b.date.month &&
          a.date.day  ===b.date.day   &&
          a.nonce     ===b.nonce
        ),
        switchMap(selected => {
          const companyId=selected.companyId;
          const year     =selected.date.year;
          const month    =selected.date.month;
          const day      =selected.date.day;

          const safeStart=<T>(observable: Observable<T>, fallback: T) =>
            observable.pipe(
              startWith(fallback),
              catchError(err => {
                this.exceptionsService.handleException(new Error(this.exceptionsService.formatHttpError(err)));
                return of(fallback);
              })
            );

          const userId=-1;//this.authenticationService.currentUserObject()?.userId ?? 0;

          return combineLatest({
            company                  : safeStart(this.companiesService.getById                      (companyId),                            <CompanyDTO | undefined>undefined),
            smsAllowance             : safeStart(this.widgetService.getCompanySMSAllowance         (userId, companyId, year, month, day), WidgetSMSAllowanceDTO_EmptyObject()),
            totalCostsUsageAndLicence: safeStart(this.widgetService.getTotalUsageAndLicenceCosts   (companyId, year, month, day),         <DashboardTotal_Previous_Current_PercentDTO[]>[]),
            totalSimCount            : safeStart(this.dashboardService.getTotalSimCount            (companyId, year, month, day),         DashboardTotal_Previous_Current_PercentDTO_EmptyObject()),
            totalUsage               : safeStart(this.dashboardService.getTotalUsage               (companyId, year, month, day),         DashboardTotal_Previous_Current_PercentDTO_EmptyObject())
          });
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(result => {
        this.applySMSAllowance             (result.smsAllowance);
        this.applyTotalCostsUsageAndLicence(result.totalCostsUsageAndLicence, result.company);
        this.applyTotalSimCount            (result.totalSimCount);
        this.applyTotalUsage               (result.totalUsage);
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private triggerFetch(): void {
    try {
      if (this.filterCompanyID===undefined || this.filterCompanyID===null)
        return;

      const date={
        year : <number>(this.filterDate?.year  ?? 1),
        month: <number>(this.filterDate?.month ?? 1),
        day  : <number>(this.filterDate?.day   ?? 1)
      };

      this.selection$.next({ companyId: this.filterCompanyID, date, nonce: this.refreshNonce });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#endregion

  //#region Public Methods

  public closeContainer(): void {
    try {
      this.visibleContainer=false;
      this.containerClosed.emit();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public collapseContainer(): void {
    try {
      this.expandContainer=false;
      this.changeDetectorRef.markForCheck();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public emitSelection(filterCompanyID: number | undefined, filterDate: OurDate | undefined): void {
    try {
      this.filterCompanyID=filterCompanyID;
      this.filterDate     =filterDate ?? populateOurDate(this.exceptionsService, Date.now());

      this.triggerFetch();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public refresh(): void {
    try {
      this.refreshNonce++;
      this.triggerFetch();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public secondaryTrendClass(sec: DashboardStatisticSecondary): string {
    try {
      const change=this.trendPercent(true, sec?.percentage) ?? 0;

      if (change===0)
        return 'text-muted';

      return (change>0)===(sec?.profitUpIsGood ?? true) ? 'text-success' : 'text-danger';
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return 'text-muted';
    }
  }

  public secondaryTrendIcon(sec: DashboardStatisticSecondary): string {
    try {
      const change=this.trendPercent(true, sec?.percentage) ?? 0;
      return change>0 ? 'ri-arrow-up-s-fill' : change<0 ? 'ri-arrow-down-s-fill' : 'ri-subtract-line';
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return '';
    }
  }

  public secondaryTrendSize(sec: DashboardStatisticSecondary): number {
    try {
      return Math.abs(this.trendPercent(true, sec?.percentage) ?? 0);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return 0;
    }
  }

  public toggleContainerExpansion(): void {
    try {
      this.expandContainer=!this.expandContainer;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public trendPercent(showProfit: boolean | undefined, percentage: string | undefined): number | null {
    try {
      if (!showProfit || percentage==null || percentage==="")
        return null;

      const percent=Number(percentage);

      return Number.isFinite(percent) ? percent : null;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return null;
    }
  }

  public usageSuffix(stat: DashboardStatistic): string | undefined {
    try {
      return this.showUsageAsTb(stat) ? 'TB' : stat?.unit;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return stat?.unit;
    }
  }

  public usageValue(stat: DashboardStatistic): string | number | null {
    try {
      if (this.showUsageAsTb(stat))
        return <number>stat.value/this.USAGE_TB_THRESHOLD_GB;

      return stat?.value ?? null;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return stat?.value ?? null;
    }
  }

  //#endregion
}
